import React, { useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { ContextualMenu, MenuDivider, MenuEntry, MenuSection, Text } from '@procsea/design-system'

import { KLARYS_TERMS_OF_USE_URL } from 'src/components/presentationals/auth/common'
import { useSessionStorageContext } from 'src/contexts/SessionStorageContext'
import { useUser } from 'src/contexts/userContext'
import { useLocale, useOptionFlags, useSegment } from 'src/hooks'
import { UserType } from 'src/types'

import MenuEntryLink from '../MenuEntryLink/MenuEntryLink'
import AccountContextMenuHeader from './AccountContextMenuHeader'
import AccountContextMenuMenuEntry from './AccountContextMenuMenuEntry'
import LanguagesContextMenu from './LanguagesContextMenu/LanguagesContextMenu'

const AccountContextMenu = () => {
  const locale = useLocale()
  const { pathname } = useLocation()
  const { optionFlagCanUseCompany } = useOptionFlags()
  const user = useUser()
  const { reset } = useSegment()
  const { setCurrentMembershipId } = useSessionStorageContext()

  const history = useHistory()

  const handleLogout = useCallback(() => {
    reset()
    setCurrentMembershipId(null)
    history.push(`/${locale}/logout`)
  }, [])

  const handleLogoutOfProfile = useCallback(() => {
    reset()
    setCurrentMembershipId(null)
    history.push(`/${locale}/logout-profile`)
  }, [])

  return (
    <ContextualMenu
      button={
        <MenuEntry
          config={{ disableActiveState: true }}
          data-e2e="menu-entry-account"
          hasChevron
          label={<AccountContextMenuMenuEntry />}
        />
      }
      data-e2e="account-context-menu"
      offset={[-12, 0]}
      placement="right-end"
      fallbackPlacements={['auto']}
    >
      <MenuSection>
        <AccountContextMenuHeader />

        <MenuDivider />

        <MenuEntryLink
          data-e2e="menu-entry-my-company"
          icon="buildings"
          isActive={pathname.includes(`/${locale}/${UserType.SELLER}/my-company/`)}
          isVisible={optionFlagCanUseCompany}
          label={gettext('My company')}
          to={`/${locale}/${UserType.SELLER}/my-company/`}
        />

        <LanguagesContextMenu />

        <MenuDivider />
        <a
          data-e2e="menu-entry-logout--anchor"
          href={`/${locale}/user/logout`}
          rel="noreferrer noopener"
        >
          <MenuEntry
            data-e2e="menu-entry-logout"
            icon="arrow-right-from-bracket"
            label={gettext('Log out')}
            onClick={handleLogout}
          />
        </a>
        {!!user?.isImpersonate && (
          <a
            data-e2e="menu-entry-stop-impersonation--anchor"
            href={`/${locale}/imp/stop/?next=/admin/`}
            rel="noreferrer noopener"
          >
            <MenuEntry
              data-e2e="menu-entry-stop-impersonation"
              icon="arrow-right-from-bracket"
              label={gettext('Log out of profile')}
              onClick={handleLogoutOfProfile}
            />
          </a>
        )}

        <MenuDivider />

        <MenuEntry
          data-e2e="menu-entry-terms-of-use"
          label={
            <Text size="small" underline>
              <a
                data-e2e="menu-entry-terms-of-use--anchor"
                href={KLARYS_TERMS_OF_USE_URL[locale]}
                rel="noreferrer noopener"
                target="_blank"
              >
                {gettext('General terms of use')}
              </a>
            </Text>
          }
        />
      </MenuSection>
    </ContextualMenu>
  )
}

export default AccountContextMenu
